<template>
  <div class="predefined-alerts-ui-container">
    <div class="predefined-alerts-ui-header">
      <span class="predefined-alerts-ui-header-title">
        <i class="fas fa-fw fa-bells"></i>
        Predefined Alerts
      </span>
      <a
        class="sc3-button sc3-button-grey sc3-button-small predefined-alerts-ui-header-summary-page-link"
        href="/freecharts/alertsummary.html"
        target="_blank"
      >
        Go to Predefined Alerts Summary&nbsp;
        <i class="fas fa-fw fa-arrow-circle-right"></i>
      </a>
    </div>
    <div class="no-predefined-alerts-found-message" v-if="hasZeroAlerts">
      No Alerts Found
    </div>
    <div class="table-responsive-overflow" v-else>
      <table class="sc3-table" v-if="predefinedAlerts">
        <thead>
          <tr>
            <th>ALERT NAME</th>
            <th>LAST TRIGGERED</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="predefinedAlert in predefinedAlerts" :key="predefinedAlert.recid" :class="{ 'is-bearish': predefinedAlert.isBearish }">
            <td><button type="button" class="sc3-button-link" @click="handleNameClick(predefinedAlert.symbol)">{{ predefinedAlert.name }}</button></td>
            <td>{{ predefinedAlert.lastFiredDateString }}</td>
          </tr>
        </tbody>
      </table>
      <div class="loading-indicator" v-else-if="predefinedAlertsLoadingState === LoadingState.LOADING">
        <i class="fad fa-fw fa-spinner-third fa-spin fa-2x"></i>
      </div>
      <div v-else-if="predefinedAlertsLoadingState === LoadingState.ERROR">
        Sorry, we failed to load the predefined alerts. We will try again in a few seconds.
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed } from 'vue';
  import { useAlerts } from '@/composables/useAlerts';
  import { LoadingState } from '@/enums/loading-state';
  import { useChartStore } from '@/stores/chartStore';
  import { scrollToChart } from '@/utils/scrollToChart';

  const { predefinedAlerts, predefinedAlertsLoadingState } = useAlerts();
  const chartStore = useChartStore();

  const hasZeroAlerts = computed(() => {
    return predefinedAlerts.value?.length === 0;
  });

  async function handleNameClick(symbol: string) {
    if (!symbol) {
      return;
    }

    chartStore.$patch({ symbol });
    await scrollToChart();
  }
</script>

<style scoped>
  .predefined-alerts-ui-container {
    margin: 0px;
    padding: 6px;
  }

  .predefined-alerts-ui-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0px 0px 6px 0px;
    margin: 0px 0px 6px 0px;
    border-bottom: 2px solid #c0ced8;
  }

  .predefined-alerts-ui-header-title {
    font-size: 16px;
    line-height: 1.5;
    font-weight: 700;
    font-family: 'Roboto Condensed', 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  }

  .predefined-alerts-ui-header-summary-page-link {
    margin: 0px;
  }

  .loading-indicator {
    text-align: center;
  }

  .no-predefined-alerts-found-message {
    padding: 10px;
    text-align: center;
    font-size: 16px;
    font-weight: bold;
  }

  table.sc3-table {
    width: calc(100% - 2px);
  }

  table.sc3-table tbody tr td {
    background-color: #eafaf2;
    color: #0e3e26;
    border: 1px solid #c1f09d;
    border-top: 0;
  }

  table.sc3-table tbody tr td .sc3-button-link {
    color: #0e3e26;
  }

  table.sc3-table tbody tr.is-bearish td {
    background-color: #fce9e8;
    border-color: #f9d4d2;
    color: #5a100c;
  }

  table.sc3-table tbody tr.is-bearish td .sc3-button-link {
    color: #5a100c;
  }
</style>
